// src/components/contentHub/ProjectSelector.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useAuthUser } from '../../hooks/useAuthUser';
import { FiLoader, FiPlusCircle, FiGrid, FiArrowRight, FiLayers } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { getPlatformDisplay } from '../shared/utils/platformHelpers';

// --- Compact Project Card for Selection ---
const SelectableProjectCard = ({ project, isSelected, onClick }) => {
    const platformDisplay = getPlatformDisplay(project);
    const PlatformIcon = platformDisplay.Icon;

    return (
        <button
            onClick={onClick}
            className={`text-left w-full p-6 rounded-[2rem] border transition-all duration-300 group/card relative overflow-hidden ${
                isSelected
                    ? 'border-slate-blue bg-slate-blue/10 shadow-glow'
                    : 'border-white/5 bg-warm-gray/10 hover:border-slate-blue/30 hover:bg-warm-gray/20'
            }`}
        >
            <div className="flex items-center gap-5">
                {project.coverImageUrl ? (
                    <img
                        src={project.coverImageUrl}
                        alt={project.projectName}
                        className="w-16 h-16 rounded-2xl object-cover flex-shrink-0 border border-white/5 shadow-md"
                    />
                ) : (
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-white/5 to-white/10 flex items-center justify-center flex-shrink-0 border border-white/5 group-hover/card:scale-105 transition-transform">
                        <PlatformIcon className={`h-7 w-7 ${platformDisplay.color}`} />
                    </div>
                )}
                <div className="min-w-0 flex-1">
                    <p className={`font-bold truncate text-lg uppercase tracking-tight transition-colors ${isSelected ? 'text-slate-blue' : 'text-cream group-hover/card:text-white'}`}>
                        {project.projectName}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                        <span className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider ${platformDisplay.color}`}>
                            <PlatformIcon size={12} />
                            {platformDisplay.name}
                        </span>
                        <span className="text-cream/20">•</span>
                        <span className="text-xs text-cream/40 truncate font-medium">{project.industry}</span>
                    </div>
                </div>
                {/* Enter Hub Indicator on Hover */}
                <div className="absolute right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover/card:opacity-100 transition-opacity">
                     <div className="bg-slate-blue/20 rounded-full p-2 border border-slate-blue/30 shadow-glow-sm">
                        <FiArrowRight className="h-5 w-5 text-slate-blue" />
                     </div>
                </div>
            </div>
        </button>
    );
};

function ProjectSelector() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { userId } = useAuthUser();

    useEffect(() => {
        if (!userId) {
            setError("User not found.");
            setLoading(false);
            return;
        }

        const fetchProjects = async () => {
            try {
                const projectsRef = collection(db, 'projects');
                const q = query(projectsRef, where('userId', '==', userId), orderBy('updatedAt', 'desc'));
                const querySnapshot = await getDocs(q);
                const userProjects = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setProjects(userProjects);
            } catch (err) {
                setError("Could not load your projects. Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, [userId]);

    const handleProjectSelection = (projectId) => {
        if (projectId) {
            // Navigate to the project-specific content hub URL
            navigate(`/project/${projectId}/content-hub`);
        }
    };

    if (loading) {
        return <div className="text-center p-10 pt-32"><FiLoader className="animate-spin mx-auto text-3xl text-slate-blue" /></div>;
    }

    if (error) {
        return <div className="text-center p-10 pt-32 text-soft-coral bg-soft-coral/10 rounded-3xl mx-4">{error}</div>;
    }

    return (
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-10 pb-32">
            
            {/* Header - Restored per user feedback */}
             <div className="flex flex-col gap-4 border-b border-white/5 pb-8 mb-10">
                <div>
                     <div className="flex items-center gap-2 mb-2">
                         <span className="px-3 py-1 bg-slate-blue/10 rounded-full border border-slate-blue/20 text-[10px] uppercase font-black tracking-widest text-slate-blue">
                             Content Hub
                         </span>
                     </div>
                    <h1 className="text-5xl font-black text-cream uppercase tracking-tighter drop-shadow-lg">
                        Select a <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-blue to-cream pr-2">Project</span>
                    </h1>
                    <p className="text-cream/50 mt-2 font-medium text-lg">Choose a project to start generating content.</p>
                </div>
            </div>

            <section className="bg-warm-gray/10 backdrop-blur-md rounded-[3rem] border border-white/5 p-10 shadow-2xl relative overflow-hidden group">
                 {/* Header Row */}
                 <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10 relative z-10">
                    <h2 className="text-sm font-black text-cream/40 uppercase tracking-widest flex items-center">
                        <FiLayers className="mr-2 h-4 w-4 text-slate-blue" /> Your Projects
                    </h2>
                    <div className="flex items-center gap-4">
                        <div className="relative group/search">
                            <FiLoader className="hidden" /> {/* Dummy loader import usage if needed, but FiSearch is needed */}
                            {/* Note: I need to ensure FiSearch is imported. It wasn't in the previous file. I'll add it. */}
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-cream/30">
                                <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                            </div>
                            <input
                                type="text"
                                placeholder="Search..."
                                disabled
                                className="pl-12 pr-6 py-3 text-sm bg-black/20 border border-white/5 rounded-full text-cream w-56 placeholder-cream/20 transition-all shadow-inner opacity-50 cursor-not-allowed"
                            />
                        </div>
                        <button className="text-xs font-bold text-slate-blue hover:text-cream flex items-center whitespace-nowrap uppercase tracking-wide transition-colors bg-white/5 hover:bg-white/10 px-4 py-3 rounded-full border border-white/5">
                            View All <FiArrowRight className="ml-2 h-3 w-3" />
                        </button>
                    </div>
                </div>
            
                <div className="space-y-4">
                    {projects.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
                            {projects.map(project => (
                                 <SelectableProjectCard 
                                    key={project.id}
                                    project={project}
                                    isSelected={false}
                                    onClick={() => handleProjectSelection(project.id)}
                                 />
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20 border-2 border-dashed border-white/5 rounded-[2rem] bg-black/10 flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mb-6">
                                <FiGrid className="h-8 w-8 text-cream/20" />
                            </div>
                            <p className="mt-2 text-lg font-medium text-cream/40">No projects available.</p>
                            <Link to="/create-project" className="mt-8 btn btn-secondary px-8 py-3 text-sm flex items-center">
                                <FiPlusCircle className="mr-2 h-5 w-5" /> Initialize Project
                            </Link>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}

export default ProjectSelector;